import React, { useEffect, useRef, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';

import queryString from 'query-string';
import styled from '@emotion/styled';
import { Button, InpockInput, InpockView, PageTitle, SmallText } from 'inpock-ui-react';
import Axios from 'axios';

import { CERT_NUM_RULE, CUSTOMER_PHONE, ORDER_NUMBER } from '@/constants';
import { useCertNumber, useForm, useToast } from '@/hooks';
import { Timer, TimerForwardedRef } from '@/components';
import { NonMemberAuthStorage } from '@/utils';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/apis';

import * as S from './styled';

const TIMEOUT_MIN = 3;
const TIMEOUT_SEC = 0;

const Redirect = () => {
  const { REACT_APP_LINK_CLIENT_URL } = process.env;

  useEffect(() => {
    window.location.href = `${REACT_APP_LINK_CLIENT_URL}/customer/menu`;
  }, []);

  return null;
};

export function AnonymousPage() {
  const navigate = useNavigate();
  const toastOn = useToast();
  const { loggedIn } = useAuth();
  const storage = new NonMemberAuthStorage();

  const [certificated, setCertificated] = useState(storage.isAvailable());
  const [isCerting, setIsCerting] = useState<boolean>(false);
  const timerRef = useRef<TimerForwardedRef>(null);

  const {
    createCertNumber,
    creatingCertNumber,
    created,
    verifyCertNumber,
    verifyingCertNumber,
    verified,
    resetCertNumber,
  } = useCertNumber();

  const { form, inputRef, register, validState } = useForm({
    phone: '',
    order_number: '',
    cert_number: '',
  });

  const submitable = Object.values(validState).every(Boolean);

  const resetPhoneValidate = async () => {
    ['phone', 'cert_number'].forEach((v) => register(v).onInput('', v));
    resetCertNumber();
    storage.clear();
    setCertificated(false);
    setIsCerting(false);
  };

  const navigateToOrderDetails = async () => {
    try {
      await api.orders.get(form.order_number, false, form.phone);
      navigate(`/anonymous/orders/${form.order_number}?phone=${form.phone}`);
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          await resetPhoneValidate();
          inputRef.current.phone?.validate();
          inputRef.current.phone?.focus();
          toastOn('비회원 인증이 만료됐어요 다시 한번 인증을 해주세요', 2000);
        } else if (error.response?.status === 404) {
          inputRef.current.order_number?.setValue('', true);
          inputRef.current.order_number?.validate();
          inputRef.current.order_number?.focus();
          toastOn('유효하지 않은 주문번호에요 다시 한번 확인해 주세요', 2000);
        }
      }
    }
  };

  const onClickGetCert = async () => {
    timerRef.current?.reset();
    try {
      await createCertNumber(form.phone);
      toastOn('SMS를 통해 인증번호가 전송되었습니다');
      setIsCerting(true);
    } catch (error) {
      if (error instanceof Error) toastOn(error.message);
      else throw error;
    }
  };

  const onClickVerify = async () => {
    if (!created) return toastOn('인증번호를 발급 받으세요');
    try {
      const { token } = await verifyCertNumber(form.phone, form.cert_number, undefined, true);
      storage.set({
        phone: form.phone,
        cert_number: form.cert_number,
        token,
        expired_at: new Date(new Date().getTime() + 30 * 60 * 1000),
      });
      setCertificated(true);
      setIsCerting(false);
      toastOn('인증에 성공했습니다');
    } catch (error) {
      if (error instanceof Error) toastOn(error.message);
      else throw error;
    }
  };

  const onClickSignup = () => {
    const { REACT_APP_LINK_CLIENT_URL } = process.env;
    window.location.href = `${REACT_APP_LINK_CLIENT_URL}/customer/signup`;
  };

  const onTimeOut = () => {
    timerRef.current?.clear();
    setIsCerting(false);
  };

  const query = queryString.parse(useLocation().search) as { phone?: string; orderNumber?: string };

  useEffect(() => {
    if (query.orderNumber) {
      register('order_number').onInput(query.orderNumber, 'order_number');
      register('order_number').onComplete(query.orderNumber, 'order_number');
    }
    if (certificated) {
      const storageData = storage.get();
      register('phone').onInput(storageData.phone, 'phone');
      register('phone').onComplete(storageData.phone, 'phone');

      register('cert_number').onInput(storageData.cert_number, 'cert_number');
      register('cert_number').onComplete(storageData.cert_number, 'cert_number');
    } else if (query.phone) {
      if (query.phone) register('phone').onInput(query.phone, 'phone');
    }
  }, []);

  if (loggedIn) return <Redirect />;

  return (
    <HelmetProvider>
      <Helmet>
        <title>주문/배송 조회 | 인포크링크</title>
      </Helmet>
      <InpockView>
        <div className="container">
          <PageTitle title={'주문/배송 조회'} icon="Back" />
          <section>
            <InpockInput
              ref={(el) => (inputRef.current['phone'] = el)}
              value={form.phone}
              {...register('phone')}
              type="text"
              inputMode="numeric"
              guide="전화번호"
              required
              width="100%"
              disabled={verified || created || certificated}
              maxLength={CUSTOMER_PHONE.rules?.max_length}
              validator={CUSTOMER_PHONE.validator}
              rules={CUSTOMER_PHONE.rules}
              placeholder="01000000000"
              onKeyPress={(value) => value.key === 'Enter' && onClickGetCert()}
              trigger={['change']}
              postfix={
                verified || created || certificated ? (
                  <Button size="medium" type="line" onClick={resetPhoneValidate}>
                    변경
                  </Button>
                ) : (
                  <Button
                    size="medium"
                    type="secondary"
                    onClick={onClickGetCert}
                    loading={creatingCertNumber}
                    disabled={
                      created || form.phone?.length < (CUSTOMER_PHONE.rules.min_length as number)
                    }
                  >
                    인증번호 받기
                  </Button>
                )
              }
            />
            {created && !verified && (
              <InpockInput
                value={form.cert_number}
                {...register('cert_number')}
                type="text"
                guide="인증번호"
                required
                disabled={certificated}
                width="100%"
                maxLength={CERT_NUM_RULE.max_length}
                onKeyPress={(value) => value.key === 'Enter' && onClickVerify()}
                postfix={
                  <div className="d-flex">
                    <div className="mr-10">
                      <Button
                        size="medium"
                        type="secondary"
                        onClick={onClickVerify}
                        disabled={
                          certificated || form.cert_number.length !== CERT_NUM_RULE.min_length
                        }
                        loading={verifyingCertNumber}
                      >
                        확인
                      </Button>
                    </div>
                    {isCerting && !certificated && (
                      <Timer
                        ref={timerRef}
                        sec={TIMEOUT_SEC}
                        min={TIMEOUT_MIN}
                        onTimeOut={onTimeOut}
                      />
                    )}
                  </div>
                }
              />
            )}

            <InpockInput
              ref={(el) => (inputRef.current['order_number'] = el)}
              value={form.order_number}
              {...register('order_number')}
              type="text"
              guide="주문번호"
              required
              width="100%"
              style={{ marginBottom: '0' }}
              placeholder="주문번호 16자를 입력해주세요"
              maxLength={ORDER_NUMBER.rules?.max_length}
              validator={ORDER_NUMBER.validator}
              rules={ORDER_NUMBER.rules}
            />
            <div className="d-flex flex-column">
              <SmallText className="text-gray-400">
                * <span className="text-orange">인포크링크 알림톡</span>으로 전송된 주문번호만
                조회할 수 있어요
              </SmallText>
              <SmallText className="text-gray-400">
                * 외부 서비스를 통한 주문은 해당 사이트에서 조회해 주세요
              </SmallText>
            </div>
            <S.BottomSheet>
              <Button
                type="primary"
                size="large"
                disabled={!submitable || !certificated}
                onClick={navigateToOrderDetails}
                fullWidth
              >
                조회하기
              </Button>
              <S.BtnSignup>
                <SmallText className="text-gray-600">
                  <span
                    className="text-orange text-underline cursor-pointer"
                    onClick={onClickSignup}
                  >
                    회원가입
                  </span>
                  하고 다음부터 간편하게 조회하기 😎
                </SmallText>
              </S.BtnSignup>
            </S.BottomSheet>
            <RegisterContainer>
              <SmallText className="text-gray-400">
                * 에이비제트(주)는 통신판매의 당사자가 아닙니다. 상품, 상품정보, 거래에 관한 의무와
                책임은 판매자에게 있습니다.
              </SmallText>
            </RegisterContainer>
          </section>
        </div>
      </InpockView>
    </HelmetProvider>
  );
}

const RegisterContainer = styled.div`
  position: fixed;
  margin: 0 auto;
  left: 0;
  right: 0;
  width: 100%;
  bottom: 20px;
  padding: 0 var(--global-margin-side);

  @media (max-height: 600px) {
    display: none;
  }

  @media (min-width: 600px) {
    max-width: 450px;
  }
`;
